import DashboardLayout from "@/components/layouts/DashboardLayout";
import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { ArrowLeft, ArrowRight, User, Calendar, Lightbulb, Link2, CheckCircle2, XCircle, Clock, MessageSquare, Phone, Video, Mail, Gift, MapPin, ShoppingCart, ExternalLink, BookOpen, Reply } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { suggestionsApi, type Recommendation, type Followup } from "@/api/suggestions";
import type { Suggestion, StatusEnum, Contact } from "@/types/api";
import { useToast } from "@/hooks/use-toast";
import { contactsApi } from "@/api/contacts";
import { contactLogsApi } from "@/api/contact-logs";
import type { ContactLog } from "@/types/api";
import { SuggestionDetailSkeleton } from "@/components/skeletons/SuggestionDetailSkeleton";
import MemoryCard from "@/components/memories/MemoryCard";
import { useQueryClient } from "@tanstack/react-query";

const SuggestionDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [contactDetails, setContactDetails] = useState<Contact | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [followups, setFollowups] = useState<Followup[]>([]);
  const [moreSuggestions, setMoreSuggestions] = useState<Suggestion[]>([]);
  const [relatedMemories, setRelatedMemories] = useState<ContactLog[]>([]);
  const [otherSuggestions, setOtherSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetchSuggestionDetails = async () => {
      if (!id) return;

      setLoading(true);
      try {
        // Fetch and display suggestion first
        const suggestionData = await suggestionsApi.get(parseInt(id));
        setSuggestion(suggestionData);
        setLoading(false); // Stop showing skeleton, show suggestion immediately

        // Get memory IDs for fetching related suggestions
        const memoryIds = suggestionData.source_logs?.map(log =>
          typeof log === 'object' && log !== null && 'id' in log ? log.id : log
        ) || [];

        // Get contact ID
        const contactId = typeof suggestionData.contact === 'object'
          ? suggestionData.contact.id
          : suggestionData.contact;

        // Fetch related memories and display immediately
        (async () => {
          if (suggestionData.source_logs && suggestionData.source_logs.length > 0) {
            console.log('Source logs found:', suggestionData.source_logs);

            const firstLog = suggestionData.source_logs[0];
            if (typeof firstLog === 'object' && firstLog !== null && 'id' in firstLog) {
              setRelatedMemories(suggestionData.source_logs as ContactLog[]);
            } else {
              // Fetch all logs in parallel
              const memoryPromises = suggestionData.source_logs.map(logId =>
                contactLogsApi.getById(logId as number).catch(error => {
                  console.error(`Failed to fetch log ID ${logId}:`, error);
                  return null;
                })
              );
              const memories = await Promise.all(memoryPromises);
              setRelatedMemories(memories.filter((m): m is ContactLog => m !== null));
            }
          }
        })();

        // Fetch and display recommendations
        suggestionsApi.getRecommendations(parseInt(id))
          .then(recommendationsData => {
            setRecommendations(recommendationsData);

            // Mark unread recommendations as read in the background
            const unreadRecommendations = recommendationsData.filter(r => !r.is_read);
            if (unreadRecommendations.length > 0) {
              Promise.all(
                unreadRecommendations.map(rec =>
                  suggestionsApi.markRecommendationAsRead(rec.id)
                )
              ).then(() => {
                queryClient.invalidateQueries({ queryKey: ['notifications-recommendations'] });
              }).catch(error => {
                console.error('Error marking recommendations as read:', error);
              });
            }
          })
          .catch(error => {
            console.log('No recommendations found or error fetching:', error);
          });

        // Fetch and display followups
        suggestionsApi.getFollowups(parseInt(id))
          .then(followupsData => {
            setFollowups(followupsData);

            // Mark unread followups as read in the background
            const unreadFollowups = followupsData.filter(f => !f.is_read);
            if (unreadFollowups.length > 0) {
              Promise.all(
                unreadFollowups.map(followup =>
                  suggestionsApi.markFollowupAsRead(followup.id)
                )
              ).then(() => {
                queryClient.invalidateQueries({ queryKey: ['followups'] });
              }).catch(error => {
                console.error('Error marking followups as read:', error);
              });
            }
          })
          .catch(error => {
            console.log('No followups found or error fetching:', error);
          });

        // Fetch and display contact details
        if (contactId) {
          contactsApi.get(contactId)
            .then(contact => setContactDetails(contact))
            .catch(error => {
              console.error('Error fetching contact details:', error);
            });
        }

        // Fetch and display memory-related suggestions
        if (memoryIds[0]) {
          suggestionsApi.list({
            memory_id: memoryIds[0],
            ordering: '-priority,-created_at',
            page_size: 10
          })
            .then(memorySuggestions => {
              console.log('Memory suggestions response:', memorySuggestions);

              const related = memorySuggestions.results?.filter(s => s.id !== suggestionData.id) || [];
              const pendingSuggestions = related.filter(s => s.status === 'pending');
              const completedSuggestions = related.filter(s => s.status !== 'pending');
              const combined = [...pendingSuggestions, ...completedSuggestions].slice(0, 3);
              setMoreSuggestions(combined);
            })
            .catch(error => {
              console.error('Error fetching memory suggestions:', error);
            });
        }

        // Fetch and display contact suggestions
        suggestionsApi.list({
          contact_id: suggestionData.contact,
          ordering: '-priority,-created_at',
          page_size: 20
        })
          .then(contactSuggestions => {
            // We need to wait a bit to get memoryRelatedIds, or filter later
            // For now, we'll just filter based on current state
            const other = contactSuggestions.results?.filter(s =>
              s.id !== suggestionData.id &&
              s.contact === suggestionData.contact
            ) || [];

            const pendingSuggestions = other.filter(s => s.status === 'pending');
            const completedSuggestions = other.filter(s => s.status !== 'pending');
            const combined = [...pendingSuggestions, ...completedSuggestions].slice(0, 3);
            setOtherSuggestions(combined);
          })
          .catch(error => {
            console.error('Error fetching contact suggestions:', error);
          });

      } catch (error) {
        console.error('Error fetching suggestion details:', error);
        setLoading(false);
      }
    };

    fetchSuggestionDetails();
  }, [id]);

  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case 3: return "text-red-600 bg-red-50";
      case 2: return "text-amber-600 bg-amber-50";
      case 1: return "text-green-600 bg-green-50";
      default: return "text-gray-600 bg-gray-50";
    }
  };

  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 3: return "High";
      case 2: return "Medium";
      case 1: return "Low";
      default: return "Unknown";
    }
  };

  const getStatusBadge = (status: StatusEnum) => {
    switch (status) {
      case "pending":
        return <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-yellow-50 text-yellow-700">
          <Clock className="w-3 h-3" /> Pending
        </span>;
      case "completed":
        return <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-green-50 text-green-700">
          <CheckCircle2 className="w-3 h-3" /> Completed
        </span>;
      case "dismissed":
        return <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-gray-50 text-gray-700">
          <XCircle className="w-3 h-3" /> Dismissed
        </span>;
      case "scheduled":
        return <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-blue-50 text-blue-700">
          <Calendar className="w-3 h-3" /> Scheduled
        </span>;
      case "expired":
        return <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-red-50 text-red-700">
          <XCircle className="w-3 h-3" /> Expired
        </span>;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const getRecommendationIcon = (type: string) => {
    const iconMap: Record<string, any> = {
      send_message: MessageSquare,
      make_call: Phone,
      video_call: Video,
      send_email: Mail,
      gift: Gift,
      flowers: Gift,
      gift_card: Gift,
      subscription: ShoppingCart,
      restaurant: MapPin,
      event_tickets: Calendar,
      movie_tickets: Calendar,
      travel: MapPin,
      activity: Calendar,
      class_workshop: Calendar,
      delivery: ShoppingCart,
      transportation: MapPin,
      accommodation: MapPin,
      spa_wellness: Calendar,
      app: ExternalLink,
      streaming: ExternalLink,
      online_course: ExternalLink,
      digital_gift: Gift,
      donation: Gift,
      custom: ExternalLink,
    };
    return iconMap[type] || ExternalLink;
  };

  const getActionLabel = (type: string) => {
    const actionMap: Record<string, string> = {
      send_message: "Send Message",
      make_call: "Call Now",
      video_call: "Start Video Call",
      send_email: "Send Email",
      gift: "Shop Now",
      flowers: "Send Flowers",
      gift_card: "Buy Gift Card",
      subscription: "Subscribe",
      restaurant: "Make Reservation",
      event_tickets: "Buy Tickets",
      movie_tickets: "Buy Tickets",
      travel: "Book Now",
      activity: "Book Activity",
      class_workshop: "Enroll Now",
      delivery: "Order Now",
      transportation: "Book Transport",
      accommodation: "Book Stay",
      spa_wellness: "Book Appointment",
      app: "Download App",
      streaming: "Subscribe",
      online_course: "Enroll",
      digital_gift: "Purchase",
      donation: "Donate",
      custom: "View Details",
    };
    return actionMap[type] || "View Details";
  };

  const handleRecommendationAction = async (rec: Recommendation) => {
    // Handle different recommendation types
    switch (rec.recommendation_type) {
      case 'send_message':
        // Open messaging app or SMS
        if (contactDetails?.phone) {
          window.location.href = `sms:${contactDetails.phone}`;
        } else {
          // Fallback to share API if no phone number
          const shareText = `Hey ${suggestion?.contact_name}, ${rec.description}`;

          if (navigator.share) {
            try {
              await navigator.share({
                title: rec.title,
                text: shareText,
              });
            } catch (error) {
              // User cancelled share or error occurred
              console.log('Share cancelled or failed', error);
            }
          } else {
            // Fallback: copy to clipboard
            try {
              await navigator.clipboard.writeText(shareText);
              toast({
                title: "Message copied",
                description: "Message copied to clipboard. You can paste it in any messaging app.",
              });
            } catch (error) {
              toast({
                title: "No phone number",
                description: "This contact doesn't have a phone number. Unable to send message.",
                variant: "destructive",
              });
            }
          }
        }
        break;
      case 'make_call':
        // Initiate phone call
        if (contactDetails?.phone) {
          window.location.href = `tel:${contactDetails.phone}`;
        } else {
          toast({
            title: "No phone number",
            description: "This contact doesn't have a phone number",
            variant: "destructive",
          });
        }
        break;
      case 'send_email':
        // Open email client
        if (contactDetails?.email) {
          window.location.href = `mailto:${contactDetails.email}`;
        } else {
          toast({
            title: "No email address",
            description: "This contact doesn't have an email address",
            variant: "destructive",
          });
        }
        break;
      case 'video_call':
        // Could integrate with video calling service
        toast({
          title: "Coming soon",
          description: "Video call functionality will be available soon!",
        });
        break;
      default:
        // For shopping/booking recommendations, open the link
        if (rec.primary_link) {
          window.open(rec.primary_link, '_blank', 'noopener,noreferrer');
        } else {
          toast({
            title: "No link available",
            description: "This recommendation doesn't have an action link",
            variant: "destructive",
          });
        }
    }
  };

  const handleComplete = async () => {
    if (!id) return;

    setActionLoading(true);
    try {
      await suggestionsApi.complete(parseInt(id));
      toast({
        title: "Success",
        description: "Suggestion marked as complete!",
      });
      // Refresh the suggestion data
      const updatedSuggestion = await suggestionsApi.get(parseInt(id));
      setSuggestion(updatedSuggestion);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to complete suggestion",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDismiss = async () => {
    if (!id) return;

    setActionLoading(true);
    try {
      await suggestionsApi.dismiss(parseInt(id));
      toast({
        title: "Success",
        description: "Suggestion dismissed",
      });
      // Navigate back to suggestions list
      navigate('/suggestions');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to dismiss suggestion",
        variant: "destructive",
      });
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <SuggestionDetailSkeleton />
      </DashboardLayout>
    );
  }

  if (!suggestion) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 md:px-8">
          <div className="py-6 md:py-8">
            <div className="bg-white rounded-lg p-12 text-center">
              <p className="text-gray-500">Suggestion not found</p>
              <Button
                onClick={() => navigate('/suggestions')}
                className="mt-4 bg-[#6C4F77] hover:bg-[#5a4063]"
              >
                Back to Suggestions
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 md:px-8">
        <div className="py-6 md:py-8">
          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Suggestion Card */}
              <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="flex items-center gap-3 cursor-pointer group"
                    onClick={() => {
                      const contactId = typeof suggestion.contact === 'object'
                        ? suggestion.contact.id
                        : suggestion.contact;
                      console.log('Navigating to contact:', contactId, typeof contactId);
                      navigate(`/contacts/${contactId}`);
                    }}
                  >
                    <Avatar className="w-16 h-16">
                      {suggestion.contact_avatar ? (
                        <AvatarImage
                          src={`/images/avatars/${suggestion.contact_avatar}`}
                          alt={suggestion.contact_name}
                        />
                      ) : null}
                      <AvatarFallback className="bg-gray-200">
                        <User className="w-8 h-8 text-gray-500" />
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-wabisabi-plum transition-colors">{suggestion.contact_name}</h3>
                      <p className="text-sm text-gray-500 capitalize">{suggestion.suggestion_type}</p>
                    </div>
                  </div>
                  {getStatusBadge(suggestion.status)}
                </div>

                {/* Title */}
                <h1 className="text-2xl font-bold text-wabisabi-plum mb-4">
                  {suggestion.title}
                </h1>

                {/* Content */}
                <p className="text-gray-700 mb-6 whitespace-pre-wrap">
                  {suggestion.content}
                </p>

                {/* Meta Info */}
                <div className="flex flex-wrap gap-4 text-sm text-gray-600 border-t pt-4">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Priority:</span>
                    <span className={`px-2 py-1 rounded-full font-medium text-xs ${getPriorityColor(suggestion.priority)}`}>
                      {getPriorityLabel(suggestion.priority)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Created:</span>
                    <span>{formatDate(suggestion.created_at)}</span>
                  </div>
                  {suggestion.completed_at && (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Completed:</span>
                      <span>{formatDate(suggestion.completed_at)}</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                {suggestion.status === "pending" && (
                  <div className="flex gap-3 mt-6">
                    <Button
                      onClick={handleComplete}
                      disabled={actionLoading}
                      className="flex-1 bg-[#6C4F77] hover:bg-[#5a4063]"
                    >
                      {actionLoading ? "Processing..." : "Mark as Complete"}
                    </Button>
                    <Button
                      onClick={handleDismiss}
                      disabled={actionLoading}
                      variant="outline"
                      className="flex-1"
                    >
                      Dismiss
                    </Button>
                  </div>
                )}
              </div>

              {/* Recommendations Section */}
              {recommendations.length > 0 && (
                <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
                  <h2 className="text-xl font-bold text-wabisabi-plum mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-blue-600" />
                    Recommendations ({recommendations.length})
                  </h2>
                  <div className="space-y-4">
                    {recommendations.map((rec) => {
                      const Icon = getRecommendationIcon(rec.recommendation_type);
                      return (
                        <div key={rec.id} className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <Icon className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900 mb-1">{rec.title}</h3>
                              <p className="text-sm text-gray-700">{rec.description}</p>
                            </div>
                          </div>

                          {(rec.price_min || rec.provider) && (
                            <div className="flex flex-wrap gap-2 text-xs text-gray-600 mb-3">
                              {rec.price_min && (
                                <span className="px-2 py-1 bg-white rounded-full">
                                  From {rec.currency} {rec.price_min}
                                </span>
                              )}
                              {rec.provider && (
                                <span className="px-2 py-1 bg-white rounded-full">{rec.provider}</span>
                              )}
                            </div>
                          )}

                          {(rec.recommendation_type === 'send_message' ||
                            rec.recommendation_type === 'make_call' ||
                            rec.recommendation_type === 'send_email' ||
                            rec.recommendation_type === 'video_call' ||
                            rec.primary_link) && (
                            <Button
                              onClick={() => handleRecommendationAction(rec)}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                              size="sm"
                            >
                              <Icon className="w-4 h-4 mr-2" />
                              {getActionLabel(rec.recommendation_type)}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Follow-ups Section */}
              {followups.length > 0 && (
                <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
                  <h2 className="text-xl font-bold text-wabisabi-plum mb-4 flex items-center gap-2">
                    <Link2 className="w-5 h-5 text-purple-600" />
                    Nudges ({followups.length})
                  </h2>
                  <div className="space-y-4">
                    {followups.map((followup) => (
                      <div key={followup.id} className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-xs px-2 py-1 bg-white rounded-full capitalize text-gray-700">
                            {followup.followup_type.replace('_', ' ')}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(followup.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{followup.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Related Memory & More Suggestions */}
            <div className="space-y-6">
              {/* Related Memory Section */}
              {relatedMemories.length > 0 && (
                <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">Related Memory</h3>
                  <div className="space-y-2">
                    {relatedMemories.map((memory) => (
                      <MemoryCard
                        key={memory.id}
                        memory={memory}
                        variant="compact"
                        showAvatar={false}
                      />
                    ))}
                  </div>
                </div>
              )}

              {moreSuggestions.length > 0 && (
                <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">More suggestions for this memory</h3>
                  <div className="space-y-4">
                    {moreSuggestions.map((sug) => (
                      <div
                        key={sug.id}
                        onClick={() => navigate(`/suggestions/${sug.id}`)}
                        className="cursor-pointer group p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100"
                      >
                        {/* Contact Info */}
                        <div className="flex items-center gap-2 mb-2">
                          <Avatar className="w-8 h-8">
                            {sug.contact_avatar ? (
                              <AvatarImage
                                src={`/images/avatars/${sug.contact_avatar}`}
                                alt={sug.contact_name}
                              />
                            ) : null}
                            <AvatarFallback className="bg-gray-200">
                              <User className="w-4 h-4 text-gray-500" />
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-900 truncate">{sug.contact_name}</p>
                            <p className="text-xs text-gray-500 capitalize">{sug.suggestion_type}</p>
                          </div>
                          {getStatusBadge(sug.status)}
                        </div>

                        {/* Title */}
                        <h4 className="text-sm font-semibold text-gray-900 mb-1 group-hover:text-wabisabi-plum transition-colors line-clamp-1">
                          {sug.title}
                        </h4>

                        {/* Content Preview */}
                        <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                          {sug.content}
                        </p>

                        {/* Priority */}
                        <div className="flex items-center justify-between">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(sug.priority)}`}>
                            {getPriorityLabel(sug.priority)}
                          </span>
                          <span className="text-xs text-wabisabi-plum group-hover:underline">
                            View →
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Other Suggestions Section */}
              {otherSuggestions.length > 0 && (
                <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">Other suggestions for {suggestion?.contact_name}</h3>
                  <div className="space-y-4">
                    {otherSuggestions.map((sug) => (
                      <div
                        key={sug.id}
                        onClick={() => navigate(`/suggestions/${sug.id}`)}
                        className="cursor-pointer group p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100"
                      >
                        {/* Contact Info */}
                        <div className="flex items-center gap-2 mb-2">
                          <Avatar className="w-8 h-8">
                            {sug.contact_avatar ? (
                              <AvatarImage
                                src={`/images/avatars/${sug.contact_avatar}`}
                                alt={sug.contact_name}
                              />
                            ) : null}
                            <AvatarFallback className="bg-gray-200">
                              <User className="w-4 h-4 text-gray-500" />
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-900 truncate">{sug.contact_name}</p>
                            <p className="text-xs text-gray-500 capitalize">{sug.suggestion_type}</p>
                          </div>
                          {getStatusBadge(sug.status)}
                        </div>

                        {/* Title */}
                        <h4 className="text-sm font-semibold text-gray-900 mb-1 group-hover:text-wabisabi-plum transition-colors line-clamp-1">
                          {sug.title}
                        </h4>

                        {/* Content Preview */}
                        <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                          {sug.content}
                        </p>

                        {/* Priority */}
                        <div className="flex items-center justify-between">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(sug.priority)}`}>
                            {getPriorityLabel(sug.priority)}
                          </span>
                          <span className="text-xs text-wabisabi-plum group-hover:underline">
                            View →
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SuggestionDetail;
